import { useCallback, useEffect, useRef, useState } from 'react';
import { ConnectionState, Track, type Room } from 'livekit-client';

import { GtoMark } from '../brand/Logo';
import { Yarlyk } from '../brand/Yarlyk';
import { Icon } from '../components/Icon';
import { QualityPanel } from '../components/QualityPanel';
import { ControlBar } from '../components/ControlBar';
import { PresentationBadge } from '../components/PresentationBadge';
import { CopyLinkButton } from '../components/CopyLinkButton';
import { RoomAudio } from '../components/RoomAudio';
import { SidePanel, type PanelTab } from '../components/SidePanel';
import { VideoGrid } from '../components/VideoGrid';
import { formatDuration } from '../lib/format';
import { MeetingRecorder, recordingSupported } from '../lib/recorder';
import { useRoomConnection, type LeaveReason } from '../lib/useRoomConnection';
import type { JoinIntent } from '../App';
import './RoomScreen.css';

const SCREEN_SHARE_SUPPORTED =
  typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getDisplayMedia);

const RECORDING_SUPPORTED = recordingSupported();

export function RoomScreen({
  intent,
  onLeave,
}: {
  intent: JoinIntent;
  onLeave: (reason: LeaveReason, message?: string) => void;
}) {
  // Панель качества сообщает наверх, открыта ли она: пока закрыта,
  // статистику незачем гонять через состояние и перерисовывать сетку.
  const [statsVisible, setStatsVisible] = useState(false);
  const connection = useRoomConnection({ intent, onLeave, statsVisible });
  const [panel, setPanel] = useState<PanelTab | null>(null);
  const elapsed = useElapsed(connection.startedAt);

  const {
    recording,
    saving: recordingSaving,
    toggle: toggleRecording,
  } = useRecording(
    connection.room,
    intent.credentials.title || intent.credentials.room,
    connection.presentation,
  );

  const { markChatRead } = connection;
  useEffect(() => {
    if (panel === 'chat') markChatRead();
  }, [panel, connection.chat.length, markChatRead]);

  // Горячие клавиши: как в привычных ВКС-клиентах.
  // Зависимости — только стабильные колбэки, иначе слушатель пересоздавался бы
  // на каждый тик таймера встречи.
  const { toggleMic, toggleCamera } = connection;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable;
      if (typing || !(event.ctrlKey || event.metaKey)) return;

      const key = event.key.toLowerCase();
      if (key === 'd') {
        event.preventDefault();
        toggleMic();
      } else if (key === 'e') {
        event.preventDefault();
        toggleCamera();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleMic, toggleCamera]);

  const connecting = connection.state !== ConnectionState.Connected;
  const reconnecting = connection.state === ConnectionState.Reconnecting;

  function togglePanel(tab: PanelTab) {
    setPanel((current) => (current === tab ? null : tab));
  }

  return (
    <div className="room">
      {connection.room && <RoomAudio room={connection.room} speakerId={intent.speakerId} />}

      {/* ── Шапка ───────────────────────────────────────────────── */}
      <header className="room__head">
        <div className="room__id">
          <GtoMark height={17} title="GTO CONNECT" />
          <span className="room__code">{intent.credentials.title || intent.credentials.room}</span>
        </div>

        <div className="room__meta">
          {reconnecting ? (
            <Yarlyk side="top" tone="danger">
              <span className="spinner room__spinner" aria-hidden />
              Восстанавливаем связь
            </Yarlyk>
          ) : (
            <span className="room__timer">
              <span className="dot dot--live" aria-hidden />
              {elapsed}
            </span>
          )}

          {/* Идёт запись — это должно быть видно, а не только по кнопке. */}
          {recording && (
            <span className="room__recording" role="status">
              <span className="room__recording-dot" aria-hidden />
              Запись
            </span>
          )}
          {connection.presentation && (
            <PresentationBadge
              isHost={connection.isHost}
              onDisable={() => connection.setPresentation(false)}
            />
          )}
          <QualityPanel
            codec={connection.codec}
            video={connection.video}
            audio={connection.audio}
            onVideoMode={connection.setVideoMode}
            onAudioMode={connection.setAudioMode}
            onNoiseSuppression={connection.setNoiseSuppression}
            onOpenChange={setStatsVisible}
          />
          <CopyLinkButton code={intent.credentials.room} label="Ссылка" variant="quiet" />
        </div>
      </header>

      {/* ── Сцена ───────────────────────────────────────────────── */}
      <main className="room__stage">
        <div className="room__video">
          {connecting && !reconnecting ? (
            <div className="room__connecting">
              <span className="spinner" />
              <p>Подключаемся к встрече</p>
            </div>
          ) : (
            <VideoGrid
              participants={connection.participants}
              presentation={connection.presentation}
            />
          )}
        </div>

        {panel && (
          <SidePanel
            tab={panel}
            code={intent.credentials.room}
            participants={connection.participants}
            chat={connection.chat}
            onTab={setPanel}
            onClose={() => setPanel(null)}
            onSend={connection.sendMessage}
          />
        )}
      </main>

      {connection.error && (
        <div className="toast" role="alert">
          <Icon name="alert" size={16} />
          {connection.error}
        </div>
      )}

      {/*
        Подсказка, а не ошибка: показ работает, просто сломается, если
        окно развернуть. Отдельный вид и крестик — чтобы её можно было
        закрыть и не путать с настоящей неполадкой.
      */}
      {!connection.error && connection.notice && (
        <div className="toast toast--info" role="status">
          <Icon name="present" size={16} />
          <span>{connection.notice}</span>
          <button
            type="button"
            className="toast__close"
            onClick={connection.dismissNotice}
            aria-label="Скрыть подсказку"
          >
            <Icon name="close" size={14} />
          </button>
        </div>
      )}

      {/* ── Управление ──────────────────────────────────────────── */}
      <ControlBar
        micEnabled={connection.micEnabled}
        cameraEnabled={connection.cameraEnabled}
        screenShareEnabled={connection.screenShareEnabled}
        screenShareSupported={SCREEN_SHARE_SUPPORTED}
        chatOpen={panel === 'chat'}
        peopleOpen={panel === 'people'}
        unreadChat={connection.unreadChat}
        participantCount={connection.participants.length}
        recordingSupported={RECORDING_SUPPORTED}
        recording={recording}
        recordingSaving={recordingSaving}
        isHost={connection.isHost}
        presentation={connection.presentation}
        onToggleMic={connection.toggleMic}
        onToggleCamera={connection.toggleCamera}
        onToggleScreenShare={connection.toggleScreenShare}
        onToggleRecording={toggleRecording}
        onTogglePresentation={() => connection.setPresentation(!connection.presentation)}
        onToggleChat={() => togglePanel('chat')}
        onTogglePeople={() => togglePanel('people')}
        onLeave={connection.leave}
      />
    </div>
  );
}

/** Длительность встречи, обновляется раз в секунду. */
function useElapsed(startedAt: number | null): string {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (startedAt === null) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [startedAt]);

  return startedAt === null ? '00:00' : formatDuration(now - startedAt);
}

/**
 * Запись встречи: старт, остановка и выдача файла.
 *
 * Ничего не уходит на сервер — см. `lib/recorder.ts`. Здесь только
 * состояние кнопки и гарантия, что при уходе со страницы недописанная
 * запись отпускается, а не остаётся висеть в памяти.
 */
function useRecording(room: Room | null, title: string, presentation: boolean) {
  const [recording, setRecording] = useState(false);
  const [saving, setSaving] = useState(false);
  const recorderRef = useRef<MeetingRecorder | null>(null);

  // Уходим со встречи — запись обрывается и пропадает. Сохранять её
  // втихую было бы обманом: человек не просил файл.
  useEffect(
    () => () => {
      recorderRef.current?.discard();
      recorderRef.current = null;
    },
    [],
  );

  const toggle = useCallback(async () => {
    if (saving) return;

    if (recorderRef.current) {
      const recorder = recorderRef.current;
      recorderRef.current = null;
      setRecording(false);
      setSaving(true);
      try {
        await recorder.stop(title);
      } finally {
        setSaving(false);
      }
      return;
    }

    const recorder = new MeetingRecorder({
      // В режиме презентации в кадре документ, а не лица: мелкий шрифт
      // на 720p расплывается, поэтому запись идёт в 1080p. В обычном
      // режиме лишние пиксели ушли бы на увеличение файла без пользы.
      quality: presentation ? 'presentation' : 'meeting',
      stage: document.querySelector<HTMLElement>('.room__video'),
      /*
       * Дорожки берём у самой комнаты, а не со страницы.
       *
       * Свой микрофон на странице не звучит — иначе было бы эхо, — и
       * через DOM его не достать. В записи же собственный голос нужен
       * в первую очередь, поэтому источник тут один и полный: свои
       * публикации плюс подписки на остальных.
       */
      audioTracks: () => {
        if (!room) return [];
        const tracks: MediaStreamTrack[] = [];

        room.localParticipant.trackPublications.forEach((publication) => {
          const track = publication.track;
          if (track?.kind === Track.Kind.Audio && track.mediaStreamTrack) {
            tracks.push(track.mediaStreamTrack);
          }
        });

        room.remoteParticipants.forEach((participant) => {
          participant.trackPublications.forEach((publication) => {
            const track = publication.track;
            if (track?.kind === Track.Kind.Audio && track.mediaStreamTrack) {
              tracks.push(track.mediaStreamTrack);
            }
          });
        });

        return tracks.filter((t) => t.readyState === 'live');
      },
    });

    try {
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
    } catch {
      recorder.discard();
    }
  }, [saving, title, room, presentation]);

  return { recording, saving, toggle };
}
